import FactCheckRounded from '@mui/icons-material/FactCheckRounded';
import TuneRounded from '@mui/icons-material/TuneRounded';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  MenuItem,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router';
import { AttachmentsPanel } from '../attachments';
import { api, unwrap } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { DataTable, type Column } from '../../components/DataTable';
import { Money } from '../../components/Money';
import { EmptyState } from '../../components/states';
import { businessDate, dateTime } from '../../lib/format';
import { useNotify } from '../../lib/notify';
import { useShop } from '../../shop/ShopProvider';
import { toAdjustmentRequest } from './forms';
import { InventoryLayout } from './InventoryLayout';
import {
  ADJUSTMENT_KIND_LABELS,
  inventoryKeys,
  idem,
  todayIn,
  useLocations,
  type StockAdjustment,
} from './model';
import { DetailHeader, SignedQty } from './shared';
import { StockEntryForm } from './StockEntryForm';

const KIND_TONE: Record<StockAdjustment['kind'], 'info' | 'warning' | 'error' | 'primary'> = {
  OPENING: 'info',
  ADJUSTMENT: 'warning',
  WRITE_OFF: 'error',
  COUNT: 'primary',
};

export function KindChip({ kind }: { kind: StockAdjustment['kind'] }) {
  const tone = KIND_TONE[kind];
  return (
    <Chip
      size="small"
      label={ADJUSTMENT_KIND_LABELS[kind]}
      sx={(t) => {
        const c = (t.vars ?? t).palette[tone].main;
        return {
          color: c,
          bgcolor: `color-mix(in srgb, ${c} 10%, transparent)`,
          border: `1px solid color-mix(in srgb, ${c} 22%, transparent)`,
        };
      }}
    />
  );
}

function netQty(a: StockAdjustment): number {
  return a.lines.reduce((s, l) => s + Number(l.qtyChange), 0);
}

export function AdjustmentsListPage() {
  const { can } = useAuth();
  const { format } = useShop();
  const navigate = useNavigate();
  const [kind, setKind] = useState<StockAdjustment['kind'] | ''>('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const query = {
    kind: kind || undefined,
    from: from || undefined,
    to: to || undefined,
    page,
    pageSize,
  };
  const list = useQuery({
    queryKey: inventoryKeys.adjustments(query),
    queryFn: () => unwrap(api.GET('/stock/adjustments', { params: { query } })),
    placeholderData: keepPreviousData,
  });
  const seesCost = can('products.view_cost');

  const columns: Column<StockAdjustment>[] = [
    {
      key: 'number',
      header: 'Number',
      render: (a) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 650 }}>
            {a.number}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {businessDate(a.businessDate, format)}
          </Typography>
        </Box>
      ),
    },
    { key: 'kind', header: 'Kind', render: (a) => <KindChip kind={a.kind} /> },
    { key: 'location', header: 'Location', hideBelow: 'md', render: (a) => a.locationName },
    {
      key: 'reason',
      header: 'Reason',
      hideBelow: 'sm',
      render: (a) => (
        <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 280 }}>
          {a.reason}
        </Typography>
      ),
    },
    {
      key: 'lines',
      header: 'Items',
      align: 'right',
      hideBelow: 'lg',
      render: (a) => a.lines.length,
    },
    {
      key: 'net',
      header: 'Net qty',
      align: 'right',
      render: (a) => <SignedQty value={netQty(a)} />,
    },
    ...(seesCost
      ? ([
          {
            key: 'value',
            header: 'Value',
            align: 'right',
            hideBelow: 'sm',
            render: (a) => <Money value={a.totalValue} signed strong />,
          },
        ] satisfies Column<StockAdjustment>[])
      : []),
  ];

  return (
    <InventoryLayout
      subtitle="Posted adjustments, write-offs, opening stock and count differences."
      actions={
        can('inventory.adjust') && (
          <Button
            variant="contained"
            startIcon={<TuneRounded />}
            onClick={() => void navigate('/inventory/adjustments/new')}
          >
            New adjustment
          </Button>
        )
      }
    >
      <DataTable
        columns={columns}
        rows={list.data?.data}
        rowKey={(a) => a.id}
        loading={list.isLoading}
        page={list.data?.page}
        onPageChange={setPage}
        onPageSizeChange={(s) => {
          setPageSize(s);
          setPage(1);
        }}
        onRowClick={(a) => void navigate(`/inventory/adjustments/${a.id}`)}
        toolbar={
          <>
            <TextField
              select
              label="Kind"
              value={kind}
              onChange={(e) => {
                setKind(e.target.value as StockAdjustment['kind'] | '');
                setPage(1);
              }}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">All kinds</MenuItem>
              {(Object.keys(ADJUSTMENT_KIND_LABELS) as StockAdjustment['kind'][]).map((k) => (
                <MenuItem key={k} value={k}>
                  {ADJUSTMENT_KIND_LABELS[k]}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              type="date"
              label="From"
              value={from}
              onChange={(e) => {
                setFrom(e.target.value);
                setPage(1);
              }}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              type="date"
              label="To"
              value={to}
              onChange={(e) => {
                setTo(e.target.value);
                setPage(1);
              }}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </>
        }
        empty={
          <EmptyState
            icon={<TuneRounded />}
            title={kind || from || to ? 'Nothing in this range' : 'No adjustments yet'}
            body="Adjust stock when you find extra items, lose some, or write off damaged goods. Every change keeps a reason."
            action={
              can('inventory.adjust')
                ? {
                    label: 'New adjustment',
                    onClick: () => void navigate('/inventory/adjustments/new'),
                  }
                : undefined
            }
          />
        }
      />
    </InventoryLayout>
  );
}

export function NewAdjustmentPage() {
  const { settings } = useShop();
  const navigate = useNavigate();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const locations = useLocations();
  // One client id per screen visit: a retried submit cannot create the adjustment twice.
  const clientId = useRef(crypto.randomUUID());
  const today = todayIn(settings?.timeZone ?? 'Asia/Karachi');
  const defaultLocation = locations.data?.find((l) => l.isDefault)?.id ?? '';

  return (
    <>
      <DetailHeader
        back="/inventory/adjustments"
        title="New stock adjustment"
        subtitle="Posts immediately to stock and the books. Choose Write-off for damage, expiry or theft."
      />
      {locations.isLoading ? (
        <Skeleton variant="rounded" height={320} />
      ) : (
        <StockEntryForm
          mode="ADJUSTMENT"
          allowModeSwitch
          defaultLocationId={defaultLocation}
          today={today}
          submitLabel="Post adjustment"
          onSubmit={async (values) => {
            try {
              const created = await unwrap(
                api.POST('/stock/adjustments', {
                  params: { header: idem(clientId.current) },
                  body: toAdjustmentRequest(values, clientId.current),
                }),
              );
              await queryClient.invalidateQueries({ queryKey: inventoryKeys.all });
              notify.success(`${created.number} posted`);
              void navigate(`/inventory/adjustments/${created.id}`, { replace: true });
            } catch (e) {
              notify.error(e);
              throw e;
            }
          }}
        />
      )}
    </>
  );
}

export function AdjustmentDetailPage() {
  const { id = '' } = useParams();
  const { format } = useShop();
  const navigate = useNavigate();
  const {
    data: a,
    isLoading,
    error,
  } = useQuery({
    queryKey: inventoryKeys.adjustment(id),
    queryFn: () => unwrap(api.GET('/stock/adjustments/{id}', { params: { path: { id } } })),
  });
  const seesCost = a?.lines.some((l) => l.unitCost !== null) ?? false;

  if (error)
    return (
      <EmptyState
        icon={<TuneRounded />}
        title="Adjustment not found"
        body="It may belong to another shop or the link is wrong."
        action={{
          label: 'Back to adjustments',
          onClick: () => void navigate('/inventory/adjustments'),
        }}
      />
    );

  return (
    <>
      <DetailHeader
        back="/inventory/adjustments"
        title={a?.number ?? 'Adjustment'}
        chips={a && <KindChip kind={a.kind} />}
        subtitle={a ? `${a.locationName} · ${businessDate(a.businessDate, format)}` : undefined}
        actions={
          a?.stockCountId ? (
            <Button
              variant="outlined"
              startIcon={<FactCheckRounded />}
              onClick={() => void navigate(`/inventory/counts/${a.stockCountId ?? ''}`)}
            >
              View count
            </Button>
          ) : undefined
        }
      />
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
          mb: 2.5,
        }}
      >
        <Fact label="Reason" value={a?.reason} wide />
        <Fact label="Items" value={a ? String(a.lines.length) : undefined} />
        <Fact label="Net quantity" value={a ? <SignedQty value={netQty(a)} /> : undefined} />
        {seesCost && (
          <Fact
            label="Value change"
            value={a ? <Money value={a.totalValue} signed strong /> : undefined}
          />
        )}
        <Fact label="Posted" value={a ? dateTime(a.postedAt, format) : undefined} />
      </Box>
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width={48}>#</TableCell>
                <TableCell>Item</TableCell>
                <TableCell align="right">Change</TableCell>
                {seesCost && (
                  <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    Unit cost
                  </TableCell>
                )}
                {seesCost && <TableCell align="right">Value</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading &&
                [0, 1, 2].map((i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={5}>
                      <Skeleton />
                    </TableCell>
                  </TableRow>
                ))}
              {a?.lines.map((l) => (
                <TableRow key={l.lineNo} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                  <TableCell className="tabular" sx={{ color: 'text.disabled' }}>
                    {l.lineNo}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {l.displayName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {l.sku}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <SignedQty value={l.qtyChange} />
                  </TableCell>
                  {seesCost && (
                    <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      <Money value={l.unitCost} />
                    </TableCell>
                  )}
                  {seesCost && (
                    <TableCell align="right">
                      <Money value={l.totalCost} signed />
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
      <AttachmentsPanel recordType="STOCK_ADJUSTMENT" recordId={id} sx={{ mt: 3 }} />
    </>
  );
}

export function Fact({ label, value, wide }: { label: string; value: ReactNode; wide?: boolean }) {
  return (
    <Card sx={{ gridColumn: wide ? { xs: '1 / -1', md: 'span 2' } : undefined }}>
      <CardContent sx={{ py: '14px !important' }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
          {label}
        </Typography>
        <Stack sx={{ mt: 0.25, minHeight: 24, justifyContent: 'center' }}>
          {value === undefined ? (
            <Skeleton width="60%" />
          ) : (
            <Typography variant="body1" sx={{ fontWeight: 600 }} component="div">
              {value}
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
